import { useState, useEffect, useCallback } from 'react'
import { UserService } from '../services/users'
import { AuthService } from '../services/auth'
import { SchoolService } from '../services/schools'
import type { User } from '../services/users'
import { formatNumber } from '../utils/formatNumber'
import { parseCsvImport } from '../utils/csvImport'

interface TeacherFormData {
  email: string
  name: string
  password: string
  school_id: string
  grade: string
  class: string
}

interface School {
  id: string
  name: string
}

interface TeacherManagerProps {
  schoolId?: string
}

const TeacherManager = ({ schoolId }: TeacherManagerProps = {}) => {
  const [teachers, setTeachers] = useState<User[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [showCSVImport, setShowCSVImport] = useState(false)
  const [editingTeacher, setEditingTeacher] = useState<User | null>(null)
  const [formData, setFormData] = useState<TeacherFormData>({
    email: '',
    name: '',
    password: '',
    school_id: '',
    grade: '',
    class: ''
  })
  const [csvData, setCsvData] = useState('')
  const [filter, setFilter] = useState({
    school_id: ''
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const loadTeachers = useCallback(async () => {
    try {
      setIsLoading(true)
      const teacherData = await UserService.getUsers({
        role: 'teacher',
        schoolId: schoolId || filter.school_id || undefined
      })
      setTeachers(teacherData)
    } catch (error) {
      console.error('Failed to load teachers:', error)
      setError('先生の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [filter.school_id, schoolId])

  useEffect(() => {
    loadTeachers()
    if (!schoolId) {
      loadSchools()
    }
  }, [loadTeachers, schoolId])

  const loadSchools = async () => {
    try {
      const schoolData = await SchoolService.getSchools()
      setSchools(schoolData)
    } catch (error) {
      console.error('Failed to load schools:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      school_id: schoolId || '',
      grade: '',
      class: ''
    })
    setEditingTeacher(null)
    setError('')
    setSuccess('')
    setShowPassword(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    try {
      if (editingTeacher) {
        // Update existing teacher
        await UserService.updateUser(editingTeacher.id, {
          email: formData.email,
          name: formData.name,
          school_id: formData.school_id,
          grade: formData.grade || null,
          class: formData.class || null
        })
        setSuccess('先生情報を更新しました')
      } else {
        // Create new teacher
        const result = await AuthService.createTeacherAccount({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          school_id: formData.school_id,
          grade: formData.grade || undefined,
          class: formData.class || undefined
        })
        
        if (result.auth_user_id) {
          setSuccess('先生アカウントを作成しました（即時ログイン可能）')
        } else {
          setSuccess('先生アカウントを作成しました（メール確認が必要な場合があります）')
        }
      }

      await loadTeachers()
      resetForm()
      setShowAddForm(false)
    } catch (error: unknown) {
      console.error('Teacher creation error:', error)
      
      let errorMessage = '先生の作成/更新に失敗しました'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'object' && error !== null) {
        const err = error as Record<string, unknown>
        if (err.message && typeof err.message === 'string') {
          errorMessage = err.message
        } else if (err.error_description && typeof err.error_description === 'string') {
          errorMessage = err.error_description
        } else if (err.details && typeof err.details === 'string') {
          errorMessage = err.details
        } else {
          errorMessage = `エラーが発生しました: ${JSON.stringify(error)}`
        }
      } else {
        errorMessage = String(error)
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (teacher: User) => {
    setEditingTeacher(teacher)
    setFormData({
      email: teacher.email,
      name: teacher.name,
      password: '',
      school_id: teacher.school_id || '',
      grade: teacher.grade || '',
      class: teacher.class || ''
    })
    setError('')
    setSuccess('')
    setShowAddForm(true)
  }

  const handleDelete = async (teacherId: string) => {
    if (!confirm('この先生を削除しますか？')) return

    try {
      setIsLoading(true)
      await UserService.deleteUser(teacherId)
      setSuccess('先生を削除しました')
      await loadTeachers()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || '先生の削除に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (teacherId: string) => {
    const newPassword = prompt('新しいパスワードを入力してください：')
    if (!newPassword) return

    try {
      setIsLoading(true)
      setError('') // Clear previous errors
      setSuccess('') // Clear previous success messages

      const teacher = teachers.find(t => t.id === teacherId)
      if (teacher) {
        await AuthService.setUserPassword(teacher.id, newPassword)
        setSuccess(`${teacher.name} のパスワードを更新しました。`)
      }
    } catch (error: unknown) {
      console.error('Password update error:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'パスワードのリセットに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  // CSV一括登録の列順（見出し行を省略したときはこの順で読む）
  const teacherCsvColumns = schoolId
    ? ['email', 'name', 'password', 'grade', 'class']
    : ['email', 'name', 'password', 'school_id', 'grade', 'class']

  const handleCSVImport = async () => {
    try {
      setIsLoading(true)
      setError('')

      // school_id is auto-filled from context when provided
      const rows = parseCsvImport(csvData, teacherCsvColumns)

      let successCount = 0
      const errors: string[] = []

      for (const { lineNumber, data: teacherData } of rows) {
        try {
          await AuthService.createTeacherAccount({
            email: teacherData.email,
            name: teacherData.name,
            password: teacherData.password,
            school_id: schoolId || teacherData.school_id,
            grade: teacherData.grade || undefined,
            class: teacherData.class || undefined
          })
          successCount++
        } catch (error) {
          errors.push(`${lineNumber}行目: ${error instanceof Error ? error.message : String(error)}`)
        }
      }

      if (errors.length > 0) {
        setError(`${successCount}件成功、${errors.length}件失敗:\n${errors.join('\n')}`)
      } else {
        setSuccess(`${successCount}件の先生アカウントを作成しました`)
      }

      setCsvData('')
      setShowCSVImport(false)
      await loadTeachers()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'CSVインポートに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">先生管理</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setError('')
              setSuccess('')
              setShowCSVImport(true)
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            CSV一括登録
          </button>
          <button
            onClick={() => {
              resetForm()
              setShowAddForm(true)
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            先生追加
          </button>
        </div>
      </div>

      {/* Filters */}
      {!schoolId && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                学校
              </label>
              <select
                value={filter.school_id}
                onChange={(e) => setFilter({...filter, school_id: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">全て</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="text-green-800 text-sm">{success}</div>
        </div>
      )}

      {/* Teacher List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            先生一覧 ({formatNumber(teachers.length)}人)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  先生情報
                </th>
                {!schoolId && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    学校
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teachers.map((teacher) => (
                <tr key={teacher.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{teacher.name}</div>
                      <div className="text-sm text-gray-500">{teacher.email}</div>
                    </div>
                  </td>
                  {!schoolId && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {schools.find(s => s.id === teacher.school_id)?.name || '未設定'}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        teacher.password_reset_required 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {teacher.password_reset_required ? 'パスワード要変更' : 'アクティブ'}
                      </span>
                      {teacher.last_login && (
                        <span className="text-xs text-gray-500">
                          最終: {new Date(teacher.last_login).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(teacher)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleResetPassword(teacher.id)}
                      className="text-yellow-600 hover:text-yellow-900"
                    >
                      パスワードリセット
                    </button>
                    <button
                      onClick={() => handleDelete(teacher.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Teacher Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTeacher ? '先生情報編集' : '新規先生追加'}
            </h3>
            
            {/* Error/Success Messages in Form */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm">{error}</div>
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-green-800 text-sm">{success}</div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  メールアドレス
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  名前
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              {!editingTeacher && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      パスワード
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, password: 'Mirapass2025'})}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      デフォルトパスワードを使用
                    </button>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                  <label className="flex items-center mt-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                      className="mr-2"
                    />
                    パスワードを表示
                  </label>
                </div>
              )}
              {!schoolId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    学校
                  </label>
                  <select
                    value={formData.school_id}
                    onChange={(e) => setFormData({...formData, school_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">選択してください</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  担当学年（任意）
                </label>
                <input
                  type="text"
                  value={formData.grade}
                  onChange={(e) => setFormData({...formData, grade: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="例: 1年、2年、3年"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  担当クラス（任意）
                </label>
                <input
                  type="text"
                  value={formData.class}
                  onChange={(e) => setFormData({...formData, class: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="例: A組、B組、1組"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? '処理中...' : (editingTeacher ? '更新' : '作成')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    resetForm()
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCSVImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              CSV一括登録
            </h3>

            {/* Error/Success Messages in CSV Import Modal */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm whitespace-pre-line">{error}</div>
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-green-800 text-sm">{success}</div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CSVデータ
                </label>
                <div className="text-sm text-gray-600 mb-2">
                  形式: {teacherCsvColumns.join(',')}（1行目の見出しは省略可）
                </div>
                <textarea
                  value={csvData}
                  onChange={(e) => setCsvData(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-40"
                  placeholder={schoolId
                    ? "teacher1@example.com,山田太郎,password123,1年,A組"
                    : "teacher1@example.com,山田太郎,password123,school-id,1年,A組"}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCSVImport}
                  disabled={isLoading || !csvData.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? '処理中...' : 'インポート'}
                </button>
                <button
                  onClick={() => {
                    setShowCSVImport(false)
                    setCsvData('')
                    setError('')
                    setSuccess('')
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeacherManager